import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from "react";
import axios from "axios";
import { fetchItems } from "../../../store/itemSlice";
import { useDispatch } from "react-redux";
import {
  Trash2,
  Edit,
  Plus,
  Printer,
  X,
  Lightbulb,
  Loader2,
  Check,
  History,
} from "lucide-react";
import { IndentPDF } from "../../../components/Header/IndentPDF";
import PrinterButton from "../IndentComponents/PrinterButton";
import SearchRow from "../IndentComponents/SearchRow";
import Modal from "../IndentComponents/Modal";
import MobileOptimizedModal from "../IndentComponents/MobileOptimizedModal";
import DeleteConfirmationModal from "../IndentComponents/DeleteConfirmationModal";
import ItemHistoryButton from "../IndentChangesTracker";
import { message } from "antd";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Tooltip = ({ content, children }) => {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef(null);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div
          ref={tooltipRef}
          className="absolute z-50 p-2 bg-gray-800 text-white text-sm rounded shadow-lg max-w-xs whitespace-normal -top-2 left-full ml-2"
        >
          {content}
        </div>
      )}
    </div>
  );
};

const ItemsTable = ({
  indentID,
  indentType,
  onAddItem,
  onDeleteItem,
  calculateTotalAmount,
  onUpdateItem,
  isDraft,
  isEditable = true,
  onSubmit,
  onSaveDraft,
  onPrint,
  indentStatus = { l1: 0, l2: 0, po: 0, ph: 0, so: 0 },
  changes,
  storedChangeLogs,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemsUpdated, setItemsUpdated] = useState(false);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [loadingDescriptions, setLoadingDescriptions] = useState({});
  const [pdfError, setPdfError] = useState("");
  const dispatch = useDispatch();
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    itemIndex: null,
    itemName: "",
  });

  const [typePeriods, setTypePeriods] = useState(null);
  const [isWithinSubmissionPeriod, setIsWithinSubmissionPeriod] = useState(true);

  // Fetch type periods and check submission period
  useEffect(() => {
    const fetchTypePeriods = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/indent/typeperiods`);
        setTypePeriods(response.data);

        if (indentType === 'normal') {
          const normalPeriod = response.data.find(type => type.TypeName.toLowerCase() === 'normal');
          if (normalPeriod) {
            const currentDate = new Date();
            const periodFrom = new Date(normalPeriod.TypePeriod);
            const periodTo = new Date(normalPeriod.TypePeriodTo);

            setIsWithinSubmissionPeriod(currentDate >= periodFrom && currentDate <= periodTo);
          }
        }
      } catch (error) {
        console.error('Error fetching type periods:', error);
      }
    };

    fetchTypePeriods();
  }, [indentType]);


  const fetchIndentData = async () => {
    const indentResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/indent/indents/${indentID}`
    );
    const indentData = await indentResponse.json();
    const indentDetails = Array.isArray(indentData)
      ? indentData[0]
      : indentData;

    // Fetch items with category information
    const itemsResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/indent/indentitems/${indentID}`
    );
    const itemsData = await itemsResponse.json();

    console.log('Indent details:', indentDetails);

    return {
      indentId: indentID,
      creationDate: indentDetails?.CreationDate,
      PEApprovalDate: indentDetails?.PEApprovalDate || indentDetails?.PEApprovedDate || null,
      L1ApprovedDate: indentDetails?.L1ApprovedDate || null,
      L2ApprovalDate: indentDetails?.L2ApprovalDate || null,
      projectName: indentDetails?.ProjectName || "N/A",
      userName: indentDetails?.UserName || "N/A",
      status: indentDetails?.Status || "N/A",
      taskScheduled: indentDetails?.TaskSchedule || "N/A",
      invoicingType: indentDetails?.TypeName || "N/A",
      // Add approval flags
      PO: indentDetails?.PO || 0,
      L1: indentDetails?.L1 || 0,
      L2: indentDetails?.L2 || 0,
      items: itemsData.map((item) => ({
        name: item.ItemName,
        category: item.CategoryName,
        au: item.UnitName,
        quantity: item.Quantity,
        stock: item.Stock,
        description: item.Description || "__",
        promisedDays: item.NormalDays,
        promisedDays2: item.EmergencyDays,
        committedDate: item.CommitedDate,
      })),
    };
  };

  // Function to get PDF component
  const getPDFComponent = (data) => {
    return <IndentPDF data={data} />;
  };

  useEffect(() => {
    if (indentID) {
      dispatch(fetchItems(indentID))
        .unwrap()
        .then((items) => {
          items.forEach((item) => {
            if (!item.description) {
              fetchItemDescription(item.itemID);
            }
          });
        })
        .catch((error) => {
          console.error("Error fetching items:", error);
        });
    }
  }, [dispatch, indentID]);

  // Modified handleDeleteItem to handle the confirmation flow
  const handleDeleteClick = (index, itemName) => {
    setDeleteConfirmation({
      isOpen: true,
      itemIndex: index,
      itemName: itemName,
    });
  };

  const handleDeleteConfirm = async () => {
    const index = deleteConfirmation.itemIndex;
    const itemToDelete = items[index];

    try {
      await axios.delete(
        `${API_BASE_URL}/viewTable/indentitems/${itemToDelete.indentItemID}`,
        {
          params: {
            indentID: indentID,
            indentItemID: itemToDelete.indentItemID,
          },
        }
      );

      if (onDeleteItem) {
        onDeleteItem(index);
      }

      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);

      setChangeLog((prev) => ({
        ...prev,
        deletedItems: [...prev.deletedItems, itemToDelete],
      }));

      onUpdateItem &&
        onUpdateItem({
          type: "delete",
          item: itemToDelete,
          changeLog,
        });
    } catch (error) {
      console.error("Error deleting item:", error.response?.data || error);
      alert("Failed to delete item. Please try again.");
    } finally {
      setDeleteConfirmation({ isOpen: false, itemIndex: null, itemName: "" });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ isOpen: false, itemIndex: null, itemName: "" });
  };

  const fetchItemDescription = async (itemId) => {
    if (loadingDescriptions[itemId] || itemDescriptions[itemId]) return;

    console.log("Fetching description for item:", itemId);
    setLoadingDescriptions((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/itemdescription/${itemId}`,
        { params: { indentId: indentID } }
      );

      console.log("Description API response:", response.data);

      if (response.data && response.data.Description) {
        setItemDescriptions((prev) => ({
          ...prev,
          [itemId]: response.data.Description,
        }));
      }
    } catch (error) {
      console.error("Error fetching description:", error);
    } finally {
      setLoadingDescriptions((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  // Add useEffect to pre-fetch descriptions for all items
  useEffect(() => {
    if (items.length > 0) {
      items.forEach((item) => {
        if (item.itemID && !itemDescriptions[item.itemID]) {
          fetchItemDescription(item.itemID);
        }
      });
    }
  }, [items.length]); // Only depend on items.length, not the entire items array

  const [newItem, setNewItem] = useState({
    categoryID: "",
    itemID: "",
    itemName: "", // Add this line
    quantity: "1",
    stock: 0,
    remarks: "",
    normalDays: 0,
    emergencyDays: 0,
    promisedDate: "",
    accountingUnitID: "",
    accountingUnitName: "",
    isSubmitting: false,
  });

  const calculatePromisedDate = (days, isEmergency) => {
    if (!days || isNaN(days)) return "";

    const today = new Date();
    const promiseDate = new Date(today);
    promiseDate.setDate(today.getDate() + parseInt(days));
    return promiseDate.toISOString().split("T")[0];
  };

  // Update the handleItemSearch function to properly use emergency days
  const handleItemSearch = useCallback(
    (selectedItem) => {
      if (!selectedItem) {
        setFilteredItems([]);
        setNewItem((prev) => ({
          ...prev,
          categoryID: "",
          itemID: "",
          itemName: "",
          accountingUnitID: "",
          accountingUnitName: "",
          stock: 0,
          normalDays: 0,
          emergencyDays: 0,
          promisedDate: "",
          commitedDate: "",
        }));
        return;
      }

      const categoryItems = allItems.filter(
        (item) => item.CategoryID === selectedItem.CategoryID
      );
      setFilteredItems(categoryItems);

      const selectedCategory = categories.find(
        (cat) => cat.CategoryID === selectedItem.CategoryID
      );

      // Use emergency days if indent type is emergency
      const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
      const days = isEmergency
        ? selectedCategory?.EmergencyDays || 0
        : selectedCategory?.NormalDays || 0;

      console.log("Days period: ", days);

      setNewItem((prev) => ({
        ...prev,
        categoryID: selectedItem.CategoryID,
        itemID: selectedItem.ItemID,
        itemName: selectedItem.ItemName,
        accountingUnitID: selectedItem.AccountingUnitID || "",
        accountingUnitName: selectedItem.AU || "",
        stock: selectedItem.Stock || 0,
        normalDays: selectedCategory?.NormalDays || 0,
        emergencyDays: selectedCategory?.EmergencyDays || 0,
        // Calculate promised date using the correct days based on indent type
        promisedDate: calculatePromisedDate(days),
        commitedDate: calculatePromisedDate(days),
      }));
    },
    [allItems, categories, indentType]
  );

  // Update handleCategoryChange to use the correct days
  const handleCategoryChange = (categoryId) => {
    if (!categoryId) {
      setNewItem((prev) => ({
        ...prev,
        categoryID: "",
        itemID: "",
        normalDays: 0,
        emergencyDays: 0,
        promisedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      }));
      setFilteredItems([]);
      return;
    }

    const parsedCategoryId = parseInt(categoryId, 10);
    const selectedCategory = categories.find(
      (cat) => cat.CategoryID === parsedCategoryId
    );

    if (selectedCategory) {
      // Use emergency days if indent type is emergency
      const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
      const days = isEmergency
        ? selectedCategory.EmergencyDays || 0
        : selectedCategory.NormalDays || 0;

      const promisedDate = calculatePromisedDate(days);

      setNewItem((prev) => ({
        ...prev,
        categoryID: parsedCategoryId,
        itemID: "",
        normalDays: selectedCategory.NormalDays || 0,
        emergencyDays: selectedCategory.EmergencyDays || 0,
        promisedDate: promisedDate,
        accountingUnitID: "",
        accountingUnitName: "",
      }));

      const filtered = allItems.filter(
        (item) => item.CategoryID === parsedCategoryId
      );
      setFilteredItems(filtered);
    }
  };

  const handleQuantityChange = (value) => {
    // Only allow integer values
    const regex = /^\d*$/;
    if (regex.test(value)) {
      setNewItem((prev) => ({
        ...prev,
        quantity: value,
      }));
    }
  };

  // Replace the existing useEffect with this updated version
  useEffect(() => {
    // Get the relevant days based on indent type
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    const relevantDays = isEmergency ? newItem.emergencyDays : newItem.normalDays;

    if (relevantDays > 0) {
      const today = new Date();
      const promiseDate = new Date(today);
      promiseDate.setDate(today.getDate() + parseInt(relevantDays));
      const formattedDate = promiseDate.toISOString().split("T")[0];

      setNewItem((prev) => ({
        ...prev,
        promisedDate: formattedDate,
      }));
    }
  }, [newItem.normalDays, newItem.emergencyDays, indentType]); // Added indentType to dependencies

  // ... existing state
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });

  // Add real-time synchronization with polling
  useEffect(() => {
    if (!indentID) return;

    // Initial fetch
    refreshItemsData();

    // Set up polling for real-time updates (every 30 seconds)
    const pollInterval = setInterval(() => {
      refreshItemsData();
    }, 30000);

    // Cleanup interval on unmount or indentID change
    return () => {
      clearInterval(pollInterval);
    };
  }, [indentID]); // Only depend on indentID

  useEffect(() => {
    const fetchReceivedQuantities = async () => {
      if (indentStatus.so === 1 && indentID) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/viewTable/receivedquantities`,
            {
              params: { indentId: indentID },
            }
          );

          // Transform the response into an object with itemID as key
          const quantities = {};
          response.data.forEach((item) => {
            quantities[item.ItemID] = {
              received: item.ReceivedQuantity || 0,
              difference: (item.Quantity || 0) - (item.ReceivedQuantity || 0),
            };
          });

          setReceivedQuantities(quantities);
        } catch (error) {
          console.error("Error fetching received quantities:", error);
        }
      }
    };

    fetchReceivedQuantities();
  }, [indentID, indentStatus.so]);

  // Fetch categories and items when the component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [
          categoryResponse,
          itemResponse,
          indentItemsResponse,
          accountingUnitsResponse,
        ] = await Promise.all([
          axios.get(`${API_BASE_URL}/dropdown/mcatdrop`),
          axios.get(`${API_BASE_URL}/dropdown/mitemdrop`),
          axios.get(`${API_BASE_URL}/viewTable/indentitems`, {
            params: { indentId: indentID },
          }),
          axios.get(`${API_BASE_URL}/dropdown/maudrop`), // New API call to fetch accounting units
        ]);

        setCategories(categoryResponse.data || []);
        setAllItems(itemResponse.data || []);

        const fetchedItems = indentItemsResponse.data
          .filter((item) => item.IndentID === indentID)
          .map((item) => ({
            indentItemID: item.IndentItemID,
            itemName: item.ItemName,
            quantity: item.Quantity,
            stock: item.Stock || 0,
            categoryID: item.CategoryID,
            itemID: item.ItemID,
            remarks: item.Description,
            promisedDate: item.PromisedDate,
            normalDays: item.NormalDays,
            emergencyDays: item.EmergencyDays,
            accountingUnitID: item.AccountingUnitID, // Use the AccountingUnitID from MItem
            unitname: item.AU,
          }));

        setItems(fetchedItems);
      } catch (error) {
        console.error(
          "Detailed error fetching data:",
          error.response?.data || error.message
        );
        setCategories([]);
        setAllItems([]);
        setItems([]);
      }
    };

    if (indentID) {
      fetchAllData();
    }
  }, [indentID]);

  const refreshItemsData = async () => {
    if (!indentID) return;

    setIsRefreshing(true);
    try {
      console.log('Fetching items for indentID:', indentID);
      const response = await axios.get(
        `${API_BASE_URL}/viewTable/indentitems`,
        {
          params: { 
            indentId: indentID,
            _t: new Date().getTime() // Add timestamp to prevent caching
          },
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      console.log('Raw API response:', response.data);

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => ({
          indentItemID: item.IndentItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          stock: item.Stock || 0,
          categoryID: item.CategoryID,
          itemID: item.ItemID,
          remarks: item.Description,
          promisedDate: item.PromisedDate,
          normalDays: item.NormalDays,
          emergencyDays: item.EmergencyDays,
          accountingUnitID: item.AccountingUnitID,
          unitname: item.UnitName,
        }));

      console.log('Processed items:', fetchedItems);
      setItems(fetchedItems);
      setItemsUpdated(false);
    } catch (error) {
      console.error("Error refreshing items:", error);
      setError("Failed to refresh items. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleItemChange = (itemId) => {
    const parsedItemId = parseInt(itemId, 10);

    // Find the selected item
    const selectedItem = filteredItems.find(
      (item) => item.ItemID === parsedItemId
    );

    if (selectedItem) {
      setNewItem((prev) => ({
        ...prev,
        itemID: parsedItemId,
        itemName: selectedItem.ItemName, // Add this line
        accountingUnitID: selectedItem?.AccountingUnitID || "",
        accountingUnitName: selectedItem?.AU || "",
        stock: selectedItem?.Stock || 0,
      }));
    }
  };

  const handleNewItemFieldChange = useCallback((field, value) => {
    if (field === "quantity") {
      // Allow up to 3 decimal places for quantity
      const formattedValue = Math.round(parseFloat(value));
      setNewItem((prev) => ({
        ...prev,
        [field]: formattedValue,
      }));
    } else if (field === "normalDays") {
      const promisedDate = calculatePromisedDate(value, false);
      setNewItem((prev) => ({
        ...prev,
        [field]: value,
        promisedDate: promisedDate,
      }));
    } else if (field === "emergencyDays") {
      const promisedDate = calculatePromisedDate(value, true);
      setNewItem((prev) => ({
        ...prev,
        [field]: value,
        promisedDate: promisedDate,
      }));
    } else {
      setNewItem((prev) => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleAddNewItem = async () => {
    if (!newItem.quantity || parseFloat(newItem.quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    const formattedQuantity = Math.round(newItem.quantity);

    // Check for duplicate item
    const isDuplicateItem = items.some(
      (existingItem) => existingItem.itemID === newItem.itemID
    );

    if (isDuplicateItem) {
      setError(
        `Item "${newItem.itemName}" is already added. Please edit the existing item or choose a different one.`
      );
      return;
    }

    const selectedItem = filteredItems.find(
      (item) => item.ItemID === newItem.itemID
    );

    const accountingUnitID = selectedItem
      ? selectedItem.AccountingUnitID
      : null;

    if (!accountingUnitID) {
      setError(
        "Could not determine the Accounting Unit for the selected item."
      );
      return;
    }

    try {
      console.log('Adding new item:', {
        IndentID: indentID,
        ItemID: newItem.itemID,
        Quantity: formattedQuantity,
        Description: newItem.remarks || "",
        AccountingUnitID: accountingUnitID,
        CategoryID: newItem.categoryID,
        Stock: newItem.stock,
        PromisedDate: newItem.promisedDate,
        NormalDays: newItem.normalDays,
        EmergencyDays: newItem.emergencyDays,
      });

      const response = await axios.post(`${API_BASE_URL}/viewTable/indentitems`, {
        IndentID: indentID,
        ItemID: newItem.itemID,
        Quantity: formattedQuantity,
        Description: newItem.remarks || "",
        AccountingUnitID: accountingUnitID,
        CategoryID: newItem.categoryID,
        Stock: newItem.stock,
        PromisedDate: newItem.promisedDate,
        NormalDays: newItem.normalDays,
        EmergencyDays: newItem.emergencyDays,
      });

      console.log('Add item response:', response.data);

      const addedItem = {
        itemID: newItem.itemID,
        itemName: newItem.itemName,
        quantity: formattedQuantity,
        stock: newItem.stock,
        categoryID: newItem.categoryID,
        remarks: newItem.remarks,
        promisedDate: newItem.promisedDate,
        normalDays: newItem.normalDays,
        emergencyDays: newItem.emergencyDays,
      };

      onUpdateItem &&
        onUpdateItem({
          type: "add",
          item: addedItem,
          changeLog,
        });

      // Reset form
      setNewItem({
        categoryID: "",
        itemID: "",
        itemName: "",
        quantity: 1,
        stock: 0,
        total: 0,
        remarks: "",
        normalDays: 0,
        emergencyDays: 0,
        promisedDate: "",
        accountingUnitID: "",
        accountingUnitName: "",
      });

      setModalOpen(false);
      setError(null);

      // Refresh items data immediately after adding
      await refreshItemsData();
      
      // Force a re-render by updating itemsUpdated
      setItemsUpdated(true);
    } catch (error) {
      console.error("Error adding item:", error.response?.data || error);
      setError(
        error.response?.data?.error || "Failed to add item. Please try again."
      );
    }
  };

  const handleDeleteItem = async (index) => {
    const itemToDelete = items[index];
    try {
      await axios.delete(
        `${API_BASE_URL}/viewTable/indentitems/${itemToDelete.indentItemID}`,
        {
          // Use query parameters instead of request body
          params: {
            indentID: indentID,
            indentItemID: itemToDelete.indentItemID,
          },
        }
      );

      // Immediately refresh the items
      //await refreshItemsData();

      if (onDeleteItem) {
        onDeleteItem(index);
      }

      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);

      // Track deleted item
      setChangeLog((prev) => ({
        ...prev,
        deletedItems: [...prev.deletedItems, itemToDelete],
      }));

      // Notify parent component about the change
      onUpdateItem &&
        onUpdateItem({
          type: "delete",
          item: itemToDelete,
          changeLog,
        });
    } catch (error) {
      console.error("Error deleting item:", error.response?.data || error);
      alert("Failed to delete item. Please try again.");
    }
  };

  const handleUpdateItem = async () => {
    try {
      if (!editingItem?.itemID || !editingItem?.indentItemID) {
        setError("Invalid item ID");
        return;
      }

      const formattedQuantity = Math.round(editingItem.quantity);

      await axios.put(
        `${API_BASE_URL}/viewTable/indentitems/${editingItem.indentItemID}`,
        {
          Quantity: formattedQuantity,
          Stock: editingItem.stock,
          Description: editingItem.description, // Add description to the update
        }
      );

      // Update local description state
      setItemDescriptions((prev) => ({
        ...prev,
        [editingItem.itemID]: editingItem.description,
      }));

      await refreshItemsData();

      setEditingItem(null);
      const updatedItems = items.map((item) => {
        if (item.indentItemID === editingItem.indentItemID) {
          return {
            ...item,
            quantity: formattedQuantity,
            stock: editingItem.stock,
            remarks: editingItem.description, // Update remarks in items array
          };
        }
        return item;
      });

      onUpdateItem({
        type: "update",
        item: editingItem,
        items: updatedItems,
      });

      setEditingItem(null);
    } catch (error) {
      console.error("Error updating item:", error);
      setError("Failed to update item. Please try again.");
    }
  };

  // Remove the problematic useEffect that was causing infinite loops
  // useEffect(() => {
  //   if (indentID) {
  //     refreshItemsData();
  //   }
  // }, [indentID]);
  // Refresh items data after updates
  useEffect(() => {
    const refreshItems = async () => {
      if (itemsUpdated && indentID) {
        try {
          console.log("Refreshing items data...");
          const response = await axios.get(
            `${API_BASE_URL}/viewTable/indentitems`,
            {
              params: { indentId: indentID },
            }
          );

          const fetchedItems = response.data
            .filter((item) => item.IndentID === indentID)
            .map((item) => ({
              indentItemID: item.IndentItemID,
              itemName: item.ItemName,
              quantity: item.Quantity,
              stock: item.Stock || 0,
              categoryID: item.CategoryID,
              itemID: item.ItemID,
              remarks: item.Description,
              promisedDate: item.PromisedDate,
              normalDays: item.NormalDays,
              emergencyDays: item.EmergencyDays,
              accountingUnitID: item.AccountingUnitID,
              unitname: item.AU,
            }));

          console.log("Refreshed items data:", fetchedItems);
          setItems(fetchedItems);
          setItemsUpdated(false);
        } catch (error) {
          console.error("Error refreshing items:", error);
        }
      }
    };

    refreshItems();
  }, [itemsUpdated, indentID]);

  const handleFinalSubmit = async () => {
    setNewItem((prev) => ({ ...prev, isSubmitting: true }));
    try {
      await onSubmit?.();
      setChangeLog({
        addedItems: [],
        deletedItems: [],
        updatedItems: [],
      });
    } catch (error) {
      console.error("Error submitting:", error);
      setNewItem((prev) => ({
        ...prev,
        error: "Failed to submit. Please try again.",
      }));
    } finally {
      setNewItem((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const isSubmittedStage = indentStatus.l1 === 1;

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+A to open add modal
      if (
        e.ctrlKey &&
        e.key.toLowerCase() === "a" &&
        !modalOpen &&
        isDraft &&
        isEditable &&
        !isSubmittedStage
      ) {
        e.preventDefault(); // Prevent default browser "Select All" behavior
        setModalOpen(true);
      }

      // Ctrl+S to save item
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault(); // Prevent the default browser action (save)

        // Trigger the "Add Item" function or button click
        document.getElementById("addItemButton").click(); // Assuming your button has this ID
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, editingItem, isDraft, isEditable, isSubmittedStage]);

  const renderDaysHeader = () => {
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    return (
      <th className="px-4 py-3 text-right">
        {isEmergency ? "Emergency Days" : "Normal Days"}
      </th>
    );
  };

  const renderDaysCell = (item) => {
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    return (
      <td className="px-4 py-3 text-right">
        {isEmergency ? item.emergencyDays : item.normalDays}
      </td>
    );
  };

  // Update the form in the modal to show appropriate days field
  const renderDaysInput = () => {
    const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
    return (
      <div className="col-span-1">
        <label className="block text-sm font-medium mb-1">
          {isEmergency ? "Days" : "Days"}
        </label>
        <input
          type="text"
          className="border rounded w-full px-3 py-2 bg-gray-100"
          value={
            isEmergency
              ? newItem.emergencyDays || "0"
              : newItem.normalDays || "0"
          }
          readOnly
        />
      </div>
    );
  };

  const handleRevert = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/l2page/indent/${indentID}/revert`);
      if (response.status === 200) {
        message.success('Indent reverted successfully');
        // Refresh the page or update the state as needed
        window.location.reload();
      }
    } catch (error) {
      console.error('Error reverting indent:', error);
      message.error('Failed to revert indent');
    }
  };

  // Modify the render of the submit button section
  const renderSubmitButtons = () => {
    if (isSubmittedStage) {
      return (
        <button
          className="bg-green-600 text-white px-5 py-2 rounded-md cursor-not-allowed opacity-50"
          disabled
        >
          Submitted
        </button>
      );
    }

    if (isDraft && isEditable) {
      return (
        <div className="flex justify-end space-x-2 mt-4">
          <button
            onClick={onSaveDraft}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Save as Draft
          </button>
          <button
            onClick={handleFinalSubmit}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Submit
          </button>
        </div>
      );
    }

    return null;
  };

  const renderActions = (item, index) => (
    <td className="px-4 py-3 text-center">
      <div className="flex justify-center space-x-2">
        {isDraft && isEditable && (
          <>
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => handleEditItem(item, index)}
            >
              <Edit size={18} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => handleDeleteClick(index, item.itemName)}
            >
              <Trash2 size={18} />
            </button>
          </>
        )}
      </div>
    </td>
  );

  const handleDescriptionChange = useCallback((e) => {
    setEditingItem((prev) => ({
      ...prev,
      description: e.target.value,
    }));
  }, []);

  const renderDescriptionCell = useCallback((itemId) => {
    if (editingItem?.itemID === itemId) {
      return (
        <div className="flex items-center space-x-2">
          <input
            key={`description-${itemId}-${editingItem.description}`}
            type="text"
            className="border rounded px-2 py-1 text-sm w-32"
            value={editingItem.description}
            onChange={handleDescriptionChange}
            autoFocus
          />
        </div>
      );
    }

    if (loadingDescriptions[itemId]) {
      return (
        <div className="w-[18px] h-[18px] mx-auto flex items-center justify-center">
          <Loader2 size={18} className="animate-spin text-blue-500" />
        </div>
      );
    }

    const description = itemDescriptions[itemId];
    return (
      <div className="flex items-center space-x-2">
        <Tooltip content={description || "No description available"}>
          <button
            className={`${description
                ? "text-yellow-500 hover:text-yellow-600"
                : "text-gray-400 hover:text-gray-600"
              } transition-colors duration-200`}
            onClick={() => {
              if (!description) {
                fetchItemDescription(itemId);
              }
            }}
          >
            <Lightbulb size={18} />
          </button>
        </Tooltip>
      </div>
    );
  }, [editingItem, itemDescriptions, loadingDescriptions, handleDescriptionChange, fetchItemDescription]);

  const handleEditItem = (item, index) => {
    // Ensure we fetch the description if it hasn't been loaded yet
    if (!itemDescriptions[item.itemID]) {
      fetchItemDescription(item.itemID);
    }

    setEditingItem({
      ...item,
      index: index,
      indentItemID: item.indentItemID,
      description: itemDescriptions[item.itemID] || "",
    });
    setItemsUpdated(true);
  };

  const renderMobileView = () => (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow p-4 space-y-3 border border-gray-200"
        >
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <p className="font-medium text-gray-900">{item.itemName}</p>
              <p className="text-sm text-gray-500">AU: {item.unitname}</p>
            </div>
            <div className="flex space-x-2">
              {isDraft && isEditable && !isSubmittedStage && (
                <>
                  <button
                    className="text-blue-500 hover:text-blue-700"
                    onClick={() => handleEditItem(item, index)}
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDeleteClick(index, item.itemName)}
                  >
                    <Trash2 size={18} />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-gray-500">Quantity</p>
              <p className="font-medium">{Math.round(item.quantity)}</p>
            </div>
            <div>
              <p className="text-gray-500">Stock</p>
              <p className="font-medium">{item.stock}</p>
            </div>
            <div>
              <p className="text-gray-500">Promised Date</p>
              <p className="font-medium">
                {new Date(item.promisedDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })}
              </p>
            </div>
            <div>
              <p className="text-gray-500">
                {(() => {
                  const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
                  return isEmergency ? "Emergency Days" : "Normal Days";
                })()}
              </p>
              <p className="font-medium">
                {(() => {
                  const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
                  return isEmergency ? item.emergencyDays : item.normalDays;
                })()}
              </p>
            </div>
          </div>

          {indentStatus.so === 1 && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-gray-500">Received Qty</p>
                <p className="font-medium">
                  {receivedQuantities[item.itemID]?.received || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Difference</p>
                <p className="font-medium">
                  {receivedQuantities[item.itemID]?.difference || 0}
                </p>
              </div>
            </div>
          )}

          {item.remarks && (
            <div className="text-sm">
              <p className="text-gray-500">Remarks</p>
              <p className="font-medium">{item.remarks}</p>
            </div>
          )}
        </div>
      ))}

      {items.length === 0 && (
        <div className="text-center text-gray-500 py-6">
          No items added to the invoice
        </div>
      )}
    </div>
  );

  const DesktopRow = memo(function DesktopRow({
    item,
    index,
    isSubmittedStage,
    isDraft,
    isEditable,
    editingItem,
    deleteConfirmation,
    receivedQuantities,
    renderDescriptionCell,
    renderDaysCell,
    onEdit,
    onDeleteClick,
    onSave,
    onCancel,
    setEditingItem,
  }) {
    return (
      <tr className="border-b hover:bg-gray-50 transition-colors duration-200">
        <td className="px-4 py-3 text-gray-500">{index + 1}</td>
        <td className="px-4 py-3 font-medium">{item.itemName}</td>
        <td className="px-4 py-3 text-center">{renderDescriptionCell(item.itemID)}</td>
        <td className="px-4 py-3 font-medium">{item.unitname}</td>
        {editingItem && editingItem.index === index ? (
          <>
            <td className="px-4 py-3">
              <input
                type="number"
                step="1"
                inputMode="numeric"
                pattern="[0-9]*"
                className="w-full border rounded px-2 py-1"
                value={editingItem.quantity}
                onChange={(e) => setEditingItem((prev) => ({ ...prev, quantity: e.target.value }))}
              />
            </td>
            <td className="px-4 py-3">
              <input
                type="number"
                step="1"
                inputMode="numeric"
                pattern="[0-9]*"
                className="w-full border rounded px-2 py-1"
                value={editingItem.stock}
                onChange={(e) => setEditingItem((prev) => ({ ...prev, stock: parseInt(e.target.value || 0, 10) }))}
              />
            </td>
            <td className="px-4 py-3 text-right">{item.promisedDate}</td>
            {!isSubmittedStage && (
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center space-x-2">
                  <button className="text-green-500 hover:text-green-700" onClick={onSave}>Save</button>
                  <button className="text-gray-500 hover:text-gray-700" onClick={onCancel}>Cancel</button>
                </div>
              </td>
            )}
          </>
        ) : (
          <>
            <td className="px-4 py-3 text-center">{Math.round(item.quantity)}</td>
            <td className="px-4 py-3 text-right">{item.stock}</td>
            <td className="px-4 py-3 text-right">{new Date(item.promisedDate).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })}</td>
            {renderDaysCell(item)}
            {receivedQuantities && (
              <>
                <td className="px-4 py-3 text-right">{receivedQuantities[item.itemID]?.received || 0}</td>
                <td className="px-4 py-3 text-right">{receivedQuantities[item.itemID]?.difference || 0}</td>
              </>
            )}
            {!isSubmittedStage && (
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center space-x-2">
                  {isDraft && isEditable && (
                    <>
                      <button className="text-blue-500 hover:text-blue-700" onClick={() => onEdit(item, index)}>
                        <Edit size={18} />
                      </button>
                      <button className="text-red-500 hover:text-red-700" onClick={() => onDeleteClick(index, item.itemName)}>
                        <Trash2 size={18} />
                      </button>
                    </>
                  )}
                </div>
                <DeleteConfirmationModal
                  isOpen={deleteConfirmation.isOpen}
                  onClose={handleDeleteCancel}
                  onConfirm={handleDeleteConfirm}
                  itemName={deleteConfirmation.itemName}
                />
              </td>
            )}
          </>
        )}
      </tr>
    );
  });

  return (
    <div className="container mx-auto p-4">
      <div className="bg-white shadow-md rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex-1">
              {isDraft && isEditable && !isSubmittedStage && (
                <div className="text-sm text-gray-500">
                  Keyboard shortcuts: Ctrl+A to add item, Ctrl+S to save
                  {modalOpen ? ", Ctrl+S to save" : ""}
                </div>
              )}
            </div>

            <h2 className="text-xl font-bold text-gray-800 text-center">
              Indent Items
            </h2>

            <div className="flex items-center space-x-2 justify-end">
              <ItemHistoryButton
                indentID={indentID}
                changes={changes}
                storedChangeLogs={storedChangeLogs}
              />

              <div
                className="text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors duration-300"
                title="Print Invoice"
              >
                <PrinterButton
                  getPDFComponent={getPDFComponent}
                  filename={`indent-${indentID}`}
                  fetchData={fetchIndentData}
                  onError={(error) => setPdfError("Failed to generate PDF. Please try again.")}
                />
              </div>

              {!isSubmittedStage && isDraft && isEditable && (
                <button
                  className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-colors duration-300"
                  onClick={() => setModalOpen(true)}
                  title="Add Item"
                >
                  <Plus size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-4">
          {/* Desktop View */}
          <div className="hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-gray-600">
                <thead className="bg-gray-100 text-gray-700 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left w-12">S.No</th>
                    <th className="px-4 py-3 text-left">Item Name</th>
                    <th className="px-4 py-3 text-center w-8"></th>
                    <th className="px-4 py-3 text-left">AU</th>
                    <th className="px-4 py-3 text-center w-24">Quantity</th>
                    <th className="px-4 py-3 text-right">Stock</th>
                    <th className="px-4 py-3 text-right">Promised Date</th>
                    {renderDaysHeader()}
                    {indentStatus.so === 1 && (
                      <>
                        <th className="px-4 py-3 text-right">Received Qty</th>
                        <th className="px-4 py-3 text-right">Difference</th>
                      </>
                    )}
                    {!isSubmittedStage && (
                      <th className="px-4 py-3 text-center w-24">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((item, index) => (
                      <DesktopRow
                        key={index}
                        item={item}
                        index={index}
                        isSubmittedStage={isSubmittedStage}
                        isDraft={isDraft}
                        isEditable={isEditable}
                        editingItem={editingItem}
                        deleteConfirmation={deleteConfirmation}
                        receivedQuantities={indentStatus.so === 1 ? receivedQuantities : null}
                        renderDescriptionCell={renderDescriptionCell}
                        renderDaysCell={renderDaysCell}
                        onEdit={handleEditItem}
                        onDeleteClick={handleDeleteClick}
                        onSave={handleUpdateItem}
                        onCancel={() => setEditingItem(null)}
                        setEditingItem={setEditingItem}
                                />
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={!isSubmittedStage ? 7 : 6}
                        className="text-center text-gray-500 py-6"
                      >
                        Loading items...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile View */}
          <div className="block md:hidden">
            {renderMobileView()}
          </div>

          <div className="mt-6 pt-4 border-t flex justify-between items-center">
            <div className="flex space-x-4">
              {renderSubmitButtons()}
            </div>
          </div>
        </div>

        {modalOpen && (
          <>
            {/* Desktop Modal - Hidden on smaller screens */}
            <div className="hidden md:block">
              <Modal
                onClose={() => {
                  setModalOpen(false);
                  setError(null);
                }}
              >
                <div className="flex flex-col">
                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {error}
                    </div>
                  )}
                  <h2 className="text-xl font-bold text-center mb-4">
                    Add New Item
                  </h2>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleAddNewItem();
                    }}
                  >
                    <SearchRow
                      allItems={allItems.map((item) => ({
                        ...item,
                        CategoryName: categories.find(
                          (cat) => cat.CategoryID === item.CategoryID
                        )?.CategoryName,
                      }))}
                      onSelectItem={handleItemSearch}
                      selectedItemId={newItem.itemID}
                    />

                    <div className="grid grid-cols-12 gap-4 mb-4">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium mb-1">
                          Accounting Unit
                        </label>
                        <input
                          type="text"
                          className="border rounded w-full px-3 py-2 bg-gray-50"
                          value={newItem.accountingUnitName || "Not set"}
                          readOnly
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">
                          Quantity
                        </label>
                        <input
                          type="number"
                          step="1"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="border rounded w-full px-3 py-2"
                          value={newItem.quantity}
                          onChange={(e) => handleQuantityChange(e.target.value)}
                          placeholder="Enter quantity"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">
                          Stock
                        </label>
                        <input
                          type="number"
                          step="1"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="border rounded w-full px-3 py-2"
                          value={newItem.stock}
                          onChange={(e) =>
                            handleNewItemFieldChange(
                              "stock",
                              parseInt(e.target.value) || 0
                            )
                          }
                          required
                        />
                      </div>
                      {renderDaysInput()}
                      <div className="col-span-4">
                        <label className="block text-sm font-medium mb-1">
                          Promised Date
                        </label>
                        <input
                          type="text"
                          className="border rounded w-full px-3 py-2 bg-gray-100"
                          value={
                            newItem.promisedDate
                              ? (() => {
                                const promisedDate = new Date(
                                  newItem.promisedDate
                                );
                                const isEmergency = indentType && indentType.toLowerCase().includes('emergency');
                                const daysUsed = isEmergency
                                  ? newItem.emergencyDays || 0
                                  : newItem.normalDays || 0;

                                const formattedGB = promisedDate.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
                                return `${formattedGB} (${daysUsed} ${isEmergency
                                    ? "Emergency"
                                    : "Normal"
                                  } Days)`;
                              })()
                              : "Not set"
                          }
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium">Remarks</label>
                      <textarea
                        key={newItem.itemID} // Add a stable key
                        className="border rounded w-full px-3 py-2"
                        value={newItem.remarks}
                        onChange={(e) =>
                          handleNewItemFieldChange("remarks", e.target.value)
                        }
                        placeholder="Optional remarks about the item"
                        rows="3"
                      />
                    </div>

                    <div className="flex space-x-4">
                      <button
                        id="addItemButton"
                        type="submit"
                        className="bg-green-500 text-white px-4 py-2 rounded"
                      >
                        Add Item
                      </button>
                      <button
                        type="button"
                        className="bg-gray-400 text-white px-4 py-2 rounded"
                        onClick={() => setModalOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </Modal>
            </div>

            <div className="block md:hidden">
              <MobileOptimizedModal
                onClose={() => {
                  setModalOpen(false);
                  setError(null);
                }}
              >
                <div className="flex flex-col md:flex-row p-6">
                  {/* Mobile-First Approach: Stacked Layout */}
                  <div className="w-full">
                    {error && (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                      </div>
                    )}
                    <h2 className="text-xl font-bold text-center mb-4">
                      Add New Item
                    </h2>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleAddNewItem();
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Category
                        </label>
                        <select
                          className="border rounded w-full px-3 py-2 mobile:text-base"
                          value={newItem.categoryID || ""}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                          required
                        >
                          <option value="" disabled>
                            Select a Category
                          </option>
                          {categories.map((category) => (
                            <option
                              key={category.CategoryID}
                              value={category.CategoryID}
                            >
                              {category.CategoryName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Item
                        </label>
                        <select
                          className="border rounded w-full px-3 py-2 mobile:text-base"
                          value={newItem.itemID || ""}
                          onChange={(e) => handleItemChange(e.target.value)}
                          disabled={!newItem.categoryID}
                          required
                        >
                          <option value="" disabled>
                            Select an Item
                          </option>
                          {filteredItems.length > 0 ? (
                            filteredItems.map((item) => (
                              <option key={item.ItemID} value={item.ItemID}>
                                {item.ItemName}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              No items available
                            </option>
                          )}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Quantity
                          </label>
                          <input
                            type="number"
                            step="1"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className="border rounded w-full px-3 py-2 mobile:text-base"
                            value={newItem.quantity}
                            onChange={(e) =>
                              handleNewItemFieldChange(
                                "quantity",
                                parseInt(e.target.value, 10) || 0
                              )
                            }
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Stock
                          </label>
                          <input
                            type="number"
                            step="1"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className="border rounded w-full px-3 py-2 mobile:text-base"
                            value={newItem.stock}
                            onChange={(e) =>
                              handleNewItemFieldChange(
                                "stock",
                                parseInt(e.target.value, 10) || 0
                              )
                            }
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Remarks
                        </label>
                        <textarea
                          key={newItem.itemID} // Add a stable key
                          className="border rounded w-full px-3 py-2 mobile:text-base"
                          value={newItem.remarks}
                          onChange={(e) =>
                            handleNewItemFieldChange("remarks", e.target.value)
                          }
                          placeholder="Optional remarks about the item"
                          rows="3"
                        />
                      </div>
                      <div className="flex space-x-3">
                        <button
                          id="addItemButton"
                          type="submit"
                          className="flex-1 bg-green-500 text-white px-4 py-2 rounded mobile:text-base"
                        >
                          Add Item
                        </button>
                        <button
                          type="button"
                          className="flex-1 bg-gray-400 text-white px-4 py-2 rounded mobile:text-base"
                          onClick={() => setModalOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Delivery Details Section - Collapsible on Mobile */}
                  <div className="w-1/2 pl-4 border-l">
                    <h3 className="text-lg font-semibold mb-4">
                      Delivery Details
                    </h3>
                    {newItem.categoryID ? (
                      <>
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-1">
                            Normal Days
                          </label>
                          <input
                            type="text"
                            className="border rounded w-full px-3 py-2 bg-gray-100"
                            value={newItem.normalDays || "Not set"}
                            readOnly
                          />
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-1">
                            Promised Date
                          </label>
                          <input
                            type="text"
                            className="border rounded w-full px-3 py-2 bg-gray-100"
                            value={
                              newItem.promisedDate
                                ? new Date(
                                  newItem.promisedDate
                                ).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })
                                : "Not set"
                            }
                            readOnly
                          />
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-1">
                            Accounting Unit
                          </label>
                          <input
                            type="text"
                            className="border rounded w-full px-3 py-2 bg-gray-100"
                            value={newItem.accountingUnitName || "Not set"}
                            readOnly
                          />
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-500">
                        Select a category to view delivery details
                      </p>
                    )}
                  </div>
                </div>
              </MobileOptimizedModal>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ItemsTable;
